import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@radix-ui/react-icons';
import api from '../services/api';
import config from '../config/config';
import DynamicTable from '../components/Documents/DynamicTable';

// Dynamic column and row interfaces based on API response
interface Column {
  class: string;
  header: string;
  key: string;
  show: number;
}

interface Row {
  [key: string]: any;
}

interface ApiResponse {
  columns: Column[];
  rows: Row[];
  document: {
    id: string;
    name: string;
    type: string;
    status: string;
    upload_date: string;
    page_count: number;
  };
}

const InvoiceViewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [apiData, setApiData] = useState<ApiResponse | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [presignedUrl, setPresignedUrl] = useState<string>('');
  
  // State for drill-down functionality
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [drillDownData, setDrillDownData] = useState<{[key: string]: any}>({});
  const [loadingDrillDown, setLoadingDrillDown] = useState<{[key: string]: boolean}>({});
  
  // State for comments popover
  const [commentsPopover, setCommentsPopover] = useState<{
    isOpen: boolean;
    comments: string[] | null;
    position: { x: number; y: number };
  }>({
    isOpen: false,
    comments: null,
    position: { x: 0, y: 0 }
  });

  // Timeout for auto-hiding popover
  const [popoverTimeout, setPopoverTimeout] = useState<NodeJS.Timeout | null>(null);
  
  const handleExportSummary = async () => {
    if (!id || !apiData?.rows) return;
    
    // Export all invoice IDs from the current document
    const invoiceIds = apiData.rows.map((row: any) => row.id);
    
    if (invoiceIds.length === 0) {
      alert('No invoices found for export');
      return;
    }
    
    try {
      
      // Call the backend API to download Excel file
      const response = await api.post(`${config.apiBaseUrl}/data/export`, {
        document_type: 'invoice',
        selected_ids: invoiceIds,
        filters: {}
      }, {
        responseType: 'blob' // Important for file downloads
      });
      
      // Create a blob from the response data
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      
      // Generate filename with timestamp
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      const filename = `invoice_summary_${apiData?.document.name.replace(/\.[^/.]+$/, '')}_${timestamp}.xlsx`;
      
      link.download = filename;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Export summary error:', error);
      alert('Export summary failed. Please try again.');
    }
  };

  const handleExportItems = async () => {
    if (!id || !apiData?.rows) return;
    
    // Export all invoice IDs from the current document
    const invoiceIds = apiData.rows.map((row: any) => row.id);
    
    if (invoiceIds.length === 0) {
      alert('No invoices found for export');
      return;
    }
    
    try {
      
      // Call the backend API to download Excel file
      const response = await api.post(`${config.apiBaseUrl}/data/export-items`, {
        document_type: 'invoice',
        selected_ids: invoiceIds,
        filters: {}
      }, {
        responseType: 'blob' // Important for file downloads
      });
      
      // Create a blob from the response data
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      
      // Generate filename with timestamp
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      const filename = `invoice_items_${apiData?.document.name.replace(/\.[^/.]+$/, '')}_${timestamp}.xlsx`;
      
      link.download = filename;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Export items error:', error);
      alert('Export items failed. Please try again.');
    }
  };

  useEffect(() => {
    const fetchInvoiceData = async () => {
      if (!id) return;
      
      try {
        setIsLoading(true);
        setError(null);
        
        // Fetch invoice details with dynamic columns
        const response = await api.get(`${config.apiBaseUrl}/data/invoice-details/${id}`);
        setApiData(response.data);
        
        // Get presigned URL from the document data in the response
        if (response.data.document && response.data.document.presigned_url) {
          setPresignedUrl(response.data.document.presigned_url);
        }
      } catch (error) {
        console.error('Error fetching invoice data:', error);
        setError(error instanceof Error ? error.message : 'An unknown error occurred');
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvoiceData();
  }, [id]);

  // Toggle drill-down for a specific row
  const toggleDrillDown = async (rowId: string, type: string) => {
    const newExpandedRows = new Set(expandedRows);
    
    if (newExpandedRows.has(rowId)) {
      // Collapse the row
      newExpandedRows.delete(rowId);
      setExpandedRows(newExpandedRows);
      
      // Remove drill-down data
      const newDrillDownData = { ...drillDownData };
      delete newDrillDownData[rowId];
      setDrillDownData(newDrillDownData);
    } else {
      // Expand the row
      newExpandedRows.add(rowId);
      setExpandedRows(newExpandedRows);
      
      // Fetch drill-down data
      await fetchDrillDownData(rowId, type);
    }
  };

  // Fetch drill-down data from API
  const fetchDrillDownData = async (rowId: string, type: string) => {
    try {
      // Set loading state for this specific row
      setLoadingDrillDown(prev => ({ ...prev, [rowId]: true }));
      
      // Get the row data to find the document_id
      const rowData = apiData?.rows.find((row: any) => row.id === rowId);
      if (!rowData) {
        console.error('Row data not found for ID:', rowId);
        return;
      }
      
      const documentId = rowData.document_id;
      if (!documentId) {
        console.error('Row missing document_id:', rowData);
        return;
      }
      
      const response = await api.post(`${config.apiBaseUrl}/data/drill-down`, {
        document_type: 'invoice',
        row_id: rowId,
        document_id: documentId
      });
      
      const drillData = response.data;
      setDrillDownData(prev => ({ ...prev, [rowId]: drillData }));
      
    } catch (error: any) {
      console.error('Error fetching drill-down data:', error);
      // Set error state for this row
      setDrillDownData(prev => ({ 
        ...prev, 
        [rowId]: { error: error.message || 'Failed to load drill-down data' }
      }));
    } finally {
      setLoadingDrillDown(prev => ({ ...prev, [rowId]: false }));
    }
  };

  // Handle document click to toggle drill-down
  const handleDocumentClick = (rowId: string, type: string) => {
    toggleDrillDown(rowId, type);
  };

  // Check if a row is expanded
  const isRowExpanded = (rowId: string) => {
    return expandedRows.has(rowId);
  };

  // Get drill-down data for a specific row
  const getDrillDownData = (rowId: string) => {
    return drillDownData[rowId] || null;
  };

  // Check if drill-down is loading for a specific row
  const isDrillDownLoading = (rowId: string) => {
    return loadingDrillDown[rowId] || false;
  };

  // Handle info column hover for comments
  const handleInfoHover = (event: React.MouseEvent, rowId: string) => {
    const rowData = apiData?.rows.find((row: any) => row.id === rowId);
    if (rowData && rowData.info && rowData.info.length > 0) {
      handleCommentsHover(event, rowData.info);
    }
  };

  // Handle comments popover on hover
  const handleCommentsHover = (event: React.MouseEvent, comments: string[] | null) => {
    if (!comments || comments.length === 0) return;
    
    // Clear any existing timeout
    if (popoverTimeout) {
      clearTimeout(popoverTimeout);
      setPopoverTimeout(null);
    }
    
    const rect = event.currentTarget.getBoundingClientRect();
    setCommentsPopover({
      isOpen: true,
      comments: comments,
      position: { x: rect.left, y: rect.bottom + 5 }
    });
  };

  // Handle mouse leave from info icon
  const handleInfoMouseLeave = () => {
    // Set a timeout to hide the popover after a short delay
    const timeout = setTimeout(() => {
      closeCommentsPopover();
    }, 300); // 300ms delay
    setPopoverTimeout(timeout);
  };

  const closeCommentsPopover = () => {
    setCommentsPopover({
      isOpen: false,
      comments: null,
      position: { x: 0, y: 0 }
    });
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (popoverTimeout) {
        clearTimeout(popoverTimeout);
      }
    };
  }, [popoverTimeout]);

  // Render drill-down content for invoice items
  const renderDrillDownContent = (rowId: string) => {
    const drillData = getDrillDownData(rowId);
    const isLoading = isDrillDownLoading(rowId);
    
    if (isLoading) {
      return (
        <div className="flex justify-center items-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
        </div>
      );
    }
    
    if (!drillData || drillData.error) {
      return (
        <div className="text-center py-4 text-gray-500 dark:text-gray-400">
          {drillData?.error || 'No drill-down data available'}
        </div>
      );
    }
    
    if ( drillData.data && drillData.data.length > 0) {
      return (
        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-100 dark:bg-gray-800">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Item No</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Item Name</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Description</th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Quantity</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Unit</th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Net Price</th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Net Worth</th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">VAT %</th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Gross Worth</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Hours</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Page</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-700 divide-y divide-gray-200 dark:divide-gray-600">
              {drillData.data.map((item: any) => (
                <tr key={item.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{item.item_no || ''}</td>
                  <td className="px-6 py-4 whitespace-normal text-sm text-gray-900 dark:text-gray-200">{item.item_name}</td>
                  <td className="px-6 py-4 whitespace-normal text-sm text-gray-900 dark:text-gray-200">{item.description || ''}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 text-right">{item.quantity || ''}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{item.unit_measure || ''}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 text-right">{item.net_price ? item.net_price : ''}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 text-right">{item.net_worth ? item.net_worth : ''}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 text-right">{item.vat_percentage ? `${item.vat_percentage}%` : ''}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 text-right">{item.gross_worth ? item.gross_worth : ''}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{item.hours || ''}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{item.page_number || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    
    return (
      <div className="text-center py-4 text-gray-500 dark:text-gray-400">
        No additional details available
      </div>
    );
  };

  // Format currency
  const formatCurrency = (amount: number | null) => {
    if (amount === null) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !apiData) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <div className="text-red-600 dark:text-red-400 mb-4">{error || 'No data available'}</div>
        <button
          onClick={() => navigate(-1)}
          className="text-primary hover:underline"
        >
          Go Back
        </button>
      </div>
    );
  }

  const { document } = apiData;
  
  if (document.status === 'Processing') {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <h1 className="text-2xl font-semibold">Processing Document...</h1>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="px-3 py-1 rounded-md bg-primary text-white hover:bg-primary/90 dark:bg-blue-600 dark:hover:bg-blue-700"
            >
              {showPreview ? 'Hide Preview' : 'Show Preview'}
            </button>
            <button
              onClick={() => navigate('/documents')}
              className="text-sm font-medium text-primary hover:underline dark:text-blue-400 dark:hover:text-blue-300"
            >
              View All Documents
            </button>
          </div>
        </div>

        <div className="flex gap-4">
          <div className={`bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden ${showPreview ? 'w-1/2' : 'w-full'} flex items-center justify-center p-8`}>
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-300">Processing your document. This may take a few minutes...</p>
            </div>
          </div>

          {showPreview && (
            <div className="w-1/2 bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
              <div className="h-full">
                <iframe
                  src={presignedUrl}
                  className="w-full h-[calc(100vh-12rem)]"
                  title="PDF Preview"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-semibold">
            {apiData.rows && apiData.rows.length > 0 
              ? `Invoice${apiData.rows.length > 1 ? 's' : ''} - ${document.name}`
              : 'Invoice Document'
            }
          </h1>
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="px-3 py-1 rounded-md bg-primary text-white hover:bg-primary/90 dark:bg-blue-600 dark:hover:bg-blue-700"
          >
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </button>
          <button
            onClick={handleExportSummary}
            className="text-sm font-medium text-primary hover:underline dark:text-blue-400 dark:hover:text-blue-300"
          >
            Export Summary
          </button>
          <button
            onClick={handleExportItems}
            className="text-sm font-medium text-primary hover:underline dark:text-blue-400 dark:hover:text-blue-300"
          >
            Export Items
          </button>
          <button
            onClick={() => navigate('/documents')}
            className="text-sm font-medium text-primary hover:underline dark:text-blue-400 dark:hover:text-blue-300"
          >
            View All Documents
          </button>
        </div>
      </div>

      <div className="flex gap-4">
        {/* Table Section */}
        <div className={`bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden ${showPreview ? 'w-1/2' : 'w-full'}`}>
          <div className="p-6">
            {apiData.columns && apiData.rows && (
              <DynamicTable 
                columns={apiData.columns}
                rows={apiData.rows}
                selectedDocuments={[]}
                onSelectDocument={() => {}}
                onSelectAll={() => {}}
                onDocumentClick={handleDocumentClick}
                onInfoClick={handleInfoHover}
                onInfoMouseLeave={handleInfoMouseLeave}
                isRowExpanded={isRowExpanded}
                renderDrillDownContent={renderDrillDownContent}
                onDownloadExcel={() => {}}
                showDrillDown={true}
                showCheckboxes={false}
              />
            )}
          </div>
        </div>

        {/* PDF Preview Section */}
        {showPreview && (
          <div className="w-1/2 bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <div className="h-full">
              <iframe
                src={presignedUrl}
                className="w-full h-[calc(100vh-12rem)]"
                title="PDF Preview"
              />
            </div>
          </div>
        )}
      </div>

      {/* Comments Popover */}
      {commentsPopover.isOpen && (
        <div 
          className="fixed z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4 max-w-md"
          style={{
            left: commentsPopover.position.x,
            top: commentsPopover.position.y
          }}
          onMouseEnter={() => {
            // Clear timeout when hovering over popover
            if (popoverTimeout) {
              clearTimeout(popoverTimeout);
              setPopoverTimeout(null);
            }
          }}
          onMouseLeave={() => {
            // Set timeout to close popover when leaving
            const timeout = setTimeout(() => {
              closeCommentsPopover();
            }, 300);
            setPopoverTimeout(timeout);
          }}
        >
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Invoice Comments
            </h3>
            <button
              onClick={closeCommentsPopover}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              ×
            </button>
          </div>
          <div className="space-y-2">
            {commentsPopover.comments?.map((comment, index) => (
              <div key={index} className="text-sm text-gray-700 dark:text-gray-300 p-2 bg-gray-50 dark:bg-gray-700 rounded">
                {comment}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceViewPage;
